/** The backorder and drop tables (reference 59, 60): each loads its own rows for the given filters. */
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { api } from '../../../api';
import type { Backorder, Drop } from '../../../api/areas/market';
import { DataTable, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { EntityRef } from '../../../components/EntityRef';
import { EmptyState, Loading } from '../../../components/States';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { BACKORDER_STATUS, CATCH_STATUS, CustomerRef, DROP_STATUS, MarketBadge, marketEnum, money } from '../shared';

function Wrap({ children }: { children: ReactNode }) {
  return <div className="px-5 pb-5">{children}</div>;
}

/** The row's own page: the bold name opens it, the chevron repeats it for the whole row. */
function NameLink({ to, name }: { to: string; name: string }) {
  return (
    <Link to={to} className="font-semibold text-primary hover:underline">
      {name}
    </Link>
  );
}

function GoButton({ to, label }: { to: string; label: string }) {
  return (
    <Button asChild variant="ghost" size="icon-xs">
      <Link to={to} aria-label={label}>
        <ChevronRight />
      </Link>
    </Button>
  );
}

export function BackordersTable({ status, domain }: { status: string; domain: string }) {
  const { t } = useTranslation('market');
  const result = useApi((signal) => api.listBackorders({ status: status || undefined, domain: domain || undefined, limit: 200 }, { signal }), [status, domain]);
  if (result.status === 'loading') return <Wrap><Loading rows={5} /></Wrap>;
  if (result.status === 'error') return <Wrap><DataError error={result.error} onRetry={result.reload} title={t('backorders.loadBackordersError')} /></Wrap>;
  const to = (b: Backorder) => `/backorders/${encodeURIComponent(b.id)}`;
  const columns: Column<Backorder>[] = [
    { key: 'domain', header: t('backorders.domain'), cell: (b) => <NameLink to={to(b)} name={b.domain_name} /> },
    { key: 'customer', header: t('backorders.customer'), className: 'max-w-56', cell: (b) => <CustomerRef id={b.customer_id} name={b.customer_name} /> },
    {
      key: 'level',
      header: t('backorders.level'),
      cell: (b) => (
        <div className="space-y-0.5">
          <div title={b.level}>{marketEnum('level', b.level)}</div>
          <div className="text-xs text-muted-foreground">{t('backorders.priority')} {b.priority_rank}</div>
        </div>
      ),
    },
    { key: 'price', header: t('backorders.price'), numeric: true, cell: (b) => <span className="whitespace-nowrap">{money(b.price, b.currency)}</span> },
    {
      key: 'status',
      header: t('backorders.status'),
      className: 'min-w-36 max-w-60 whitespace-normal',
      cell: (b) => (
        <div className="space-y-1">
          <MarketBadge group="backorderStatus" map={BACKORDER_STATUS} value={b.status} />
          {b.status_reason ? <div className="text-xs text-muted-foreground">{b.status_reason}</div> : null}
        </div>
      ),
    },
    {
      key: 'auction',
      header: t('backorders.auction'),
      cell: (b) =>
        b.auction_id ? (
          <EntityRef label={t('backorders.auction')} id={b.auction_id} to={`/auctions/${encodeURIComponent(b.auction_id)}`} />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'placed',
      header: t('backorders.placed'),
      cell: (b) => (
        <span className="whitespace-nowrap" title={formatDateTime(b.created_at)}>
          {formatRelative(b.created_at)}
        </span>
      ),
    },
    {
      key: 'go',
      header: <span className="sr-only">{t('backorders.openBackorder', { domain: '' })}</span>,
      className: 'w-8',
      cell: (b) => <GoButton to={to(b)} label={t('backorders.openBackorder', { domain: b.domain_name })} />,
    },
  ];
  return <DataTable columns={columns} rows={result.data.items} rowKey={(b) => b.id} empty={<EmptyState title={t('backorders.noBackorders')} />} />;
}

export function DropsTable({ status, domain }: { status: string; domain: string }) {
  const { t } = useTranslation('market');
  const result = useApi((signal) => api.listDrops({ status: status || undefined, domain: domain || undefined, limit: 200 }, { signal }), [status, domain]);
  if (result.status === 'loading') return <Wrap><Loading rows={5} /></Wrap>;
  if (result.status === 'error') return <Wrap><DataError error={result.error} onRetry={result.reload} title={t('backorders.loadDropsError')} /></Wrap>;
  const to = (d: Drop) => `/drops/${encodeURIComponent(d.id)}`;
  const columns: Column<Drop>[] = [
    { key: 'domain', header: t('backorders.domain'), cell: (d) => <NameLink to={to(d)} name={d.domain_name} /> },
    { key: 'registry', header: t('backorders.registry'), className: 'max-w-40 whitespace-normal', cell: (d) => <span className="text-sm">{d.registry_name ?? '—'}</span> },
    {
      key: 'expected',
      header: t('backorders.expectedDrop'),
      cell: (d) => (
        <div className="space-y-0.5 whitespace-nowrap">
          <div className="text-xs">{formatDateTime(d.expected_drop_at)}</div>
          <div className="text-xs text-muted-foreground">{formatRelative(d.expected_drop_at)}</div>
        </div>
      ),
    },
    {
      key: 'source',
      header: t('backorders.source'),
      cell: (d) => (
        <div className="space-y-0.5">
          <div title={d.source}>{marketEnum('dropSource', d.source)}</div>
          {d.source_reference ? <div className="text-xs text-muted-foreground">{d.source_reference}</div> : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('backorders.status'),
      className: 'min-w-40 max-w-60 whitespace-normal',
      cell: (d) => (
        <div className="space-y-1">
          <MarketBadge group="dropStatus" map={DROP_STATUS} value={d.status} />
          {d.auction_id ? (
            <div className="text-xs">
              <Link to={`/auctions/${encodeURIComponent(d.auction_id)}`} className="text-primary hover:underline">
                {t('backorders.auction')}
              </Link>
            </div>
          ) : null}
          {d.status_reason ? <div className="text-xs text-muted-foreground">{d.status_reason}</div> : null}
        </div>
      ),
    },
    {
      key: 'catch',
      header: t('backorders.dropCatch'),
      className: 'min-w-44 max-w-56 whitespace-normal',
      cell: (d) =>
        d.attempt ? (
          <div className="space-y-1">
            <MarketBadge group="catchStatus" map={CATCH_STATUS} value={d.attempt.status} />
            <div className="text-xs">
              <span className="text-muted-foreground">{t('backorders.for')} </span>
              <CustomerRef id={d.attempt.customer_id} name={d.attempt.customer_name} />
              <span className="whitespace-nowrap"> · {money(d.attempt.amount, d.attempt.currency)}</span>
            </div>
            {d.attempt.outcome_reason ? <div className="text-xs text-muted-foreground">{d.attempt.outcome_reason}</div> : null}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'go',
      header: <span className="sr-only">{t('backorders.openDrop', { domain: '' })}</span>,
      className: 'w-8',
      cell: (d) => <GoButton to={to(d)} label={t('backorders.openDrop', { domain: d.domain_name })} />,
    },
  ];
  return <DataTable columns={columns} rows={result.data.items} rowKey={(d) => d.id} empty={<EmptyState title={t('backorders.noDrops')} />} />;
}
